import React, { useMemo, useState, useEffect } from "react";
import { toast } from "sonner";
import { PoundSterling, TrendingUp, TrendingDown, Wallet, Plus, Sparkles, Trash2, ShoppingCart } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "../../lib/api";

const Glass = ({ className = "", children, ...rest }) => (
    <div {...rest} className={`rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.35)] ${className}`}>{children}</div>
);

const gbp = (n) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(Number(n) || 0);
const fmtDay = (iso) => { try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); } catch { return iso; } };
const fmtFull = (iso) => { try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); } catch { return iso; } };
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const pad = (n) => String(n).padStart(2, "0");
// Typed date -> "YYYY-MM-DD". Accepts "Sep 19th", "19th Sep", "19 September 2025", "19/09/2025", "today". No year = this year.
export const parseDay = (raw) => {
    const s = (raw || "").trim().toLowerCase();
    if (!s) return null;
    const now = new Date();
    const mk = (y, m, d) => { const dt = new Date(y, m, d); return dt.getFullYear() === y && dt.getMonth() === m && dt.getDate() === d ? `${y}-${pad(m + 1)}-${pad(d)}` : null; };
    if (s === "today") return mk(now.getFullYear(), now.getMonth(), now.getDate());
    if (s === "yesterday") { const y = new Date(now.getTime() - 864e5); return mk(y.getFullYear(), y.getMonth(), y.getDate()); }
    let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (m) return mk(+m[1], +m[2] - 1, +m[3]);
    m = s.match(/^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2}|\d{4}))?$/);
    if (m) return mk(m[3] ? (m[3].length === 2 ? 2000 + +m[3] : +m[3]) : now.getFullYear(), +m[2] - 1, +m[1]);
    const toks = s.replace(/[,.]/g, " ").split(/\s+/).filter(Boolean);
    const mon = toks.findIndex((t) => /^[a-z]+$/.test(t) && t.length >= 3 && MONTHS.some((x) => t.startsWith(x)));
    if (mon < 0) return null;
    const month = MONTHS.findIndex((x) => toks[mon].startsWith(x));
    const yTok = toks.find((t) => /^\d{4}$/.test(t));
    const dTok = toks.find((t) => /^\d{1,2}(st|nd|rd|th)?$/.test(t));
    if (!dTok) return null;
    return mk(yTok ? +yTok : now.getFullYear(), month, parseInt(dTok, 10));
};
// Free-typed date box with a live read-back of what it understood.
const DateInput = ({ value, onChange, testid, onEnter }) => {
    const ymd = parseDay(value);
    return (
        <div className="relative">
            <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Date, e.g. Sep 19th" data-testid={testid}
                onKeyDown={(e) => { if (e.key === "Enter") onEnter?.(); }}
                className="w-full bg-black/30 border border-white/10 rounded-lg pl-3 pr-28 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#9D4CDD]" />
            <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-bold ${ymd ? "text-white/40" : "text-[#EF4444]/80"}`} data-testid={`${testid}-readback`}>
                {ymd ? fmtFull(`${ymd}T12:00:00`) : value.trim() ? "Not a date" : ""}
            </span>
        </div>
    );
};
// Inline editable date on an existing transaction.
const EntryDate = ({ iso, onCommit, testid }) => {
    const [txt, setTxt] = useState(fmtFull(iso));
    useEffect(() => { setTxt(fmtFull(iso)); }, [iso]);
    const commit = () => {
        const ymd = parseDay(txt);
        if (!ymd) { toast.error("Couldn't read that date. Try 'Sep 19th' or '19 Sep 2025'"); setTxt(fmtFull(iso)); return; }
        if (ymd !== iso.slice(0, 10) || fmtFull(`${ymd}T12:00:00`) !== fmtFull(iso)) onCommit(ymd);
        setTxt(fmtFull(`${ymd}T12:00:00`));
    };
    return (
        <input value={txt} onChange={(e) => setTxt(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            data-testid={testid} title="Type a new date, e.g. Sep 19th" size={Math.max(txt.length, 6)}
            className="bg-transparent border-b border-dashed border-white/20 hover:border-white/40 focus:border-[#9D4CDD] focus:outline-none text-[11px] text-white/60 focus:text-white px-0 py-0" />
    );
};

// Pretend subscription plans — each theoretically adds positive income to the graph.
const PLANS = [
    { k: "pro", label: "Rosterly Pro", price: 9.99 },
    { k: "team", label: "Team", price: 24.99 },
    { k: "enterprise", label: "Enterprise", price: 99.0 },
];

const keyFor = (u) => `rosterly_finances_${u || "guest"}`;

export default function MyFinances({ userKey }) {
    const storeKey = keyFor(userKey);
    const [entries, setEntries] = useState(() => {
        try { return JSON.parse(localStorage.getItem(storeKey) || "[]"); } catch { return []; }
    });
    const [title, setTitle] = useState("");
    const [amount, setAmount] = useState("");
    const [plan, setPlan] = useState("pro");
    const [pDate, setPDate] = useState(() => fmtFull(new Date().toISOString()));
    const [sDate, setSDate] = useState(() => fmtFull(new Date().toISOString()));
    // Entries use the date you type (noon local, so it never slips a day).
    const isoOf = (d) => (d ? new Date(`${d}T12:00:00`).toISOString() : new Date().toISOString());

    // Persist SERVER-SIDE (tied to the user account) so finances survive preview
    // forks / domain changes. localStorage was the old store and lost data on
    // every fork because each fork gets a new preview origin.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/account/finances");
                const server = Array.isArray(data?.entries) ? data.entries : [];
                if (cancelled) return;
                if (server.length) {
                    setEntries(server);
                    try { localStorage.setItem(storeKey, JSON.stringify(server)); } catch { /* ignore */ }
                } else {
                    // One-time migration: push any locally-cached entries up to the server.
                    let local = [];
                    try { local = JSON.parse(localStorage.getItem(storeKey) || "[]"); } catch { local = []; }
                    if (local.length) { api.put("/account/finances", { entries: local }).catch(() => {}); }
                }
            } catch { /* not signed in / offline — fall back to localStorage cache */ }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeKey]);

    const persist = (next) => {
        setEntries(next);
        try { localStorage.setItem(storeKey, JSON.stringify(next)); } catch { /* ignore */ }
        api.put("/account/finances", { entries: next }).catch(() => {});
    };

    const addEntry = (e) => persist([...entries, { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, ...e }]);

    const addPurchase = () => {
        const amt = parseFloat(amount);
        if (!title.trim()) { toast.error("Give the purchase a title"); return; }
        if (!amt || amt <= 0) { toast.error("Enter an amount greater than £0"); return; }
        const ymd = parseDay(pDate);
        if (!ymd) { toast.error("Couldn't read the date. Try 'Sep 19th' or '19 Sep 2025'"); return; }
        addEntry({ type: "purchase", title: title.trim(), amount: Math.round(amt * 100) / 100, date: isoOf(ymd) });
        setTitle(""); setAmount("");
        toast.success(`Added purchase · ${gbp(amt)}`);
    };
    const addSubscription = () => {
        const p = PLANS.find((x) => x.k === plan);
        if (!p) return;
        const ymd = parseDay(sDate);
        if (!ymd) { toast.error("Couldn't read the date. Try 'Sep 19th' or '19 Sep 2025'"); return; }
        addEntry({ type: "subscription", title: `${p.label} subscription`, amount: p.price, date: isoOf(ymd) });
        toast.success(`Subscribed · ${p.label} (+${gbp(p.price)})`);
    };
    const remove = (id) => persist(entries.filter((x) => x.id !== id));
    const setDate = (id, ymd) => { persist(entries.map((x) => (x.id === id ? { ...x, date: isoOf(ymd) } : x))); toast.success(`Date changed to ${fmtFull(isoOf(ymd))}`); };

    const { income, expenses, net, chartData } = useMemo(() => {
        const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));
        let bal = 0; const data = [];
        let inc = 0, exp = 0;
        sorted.forEach((e) => {
            if (e.type === "subscription") { bal += e.amount; inc += e.amount; }
            else { bal -= e.amount; exp += e.amount; }
            data.push({ label: fmtDay(e.date), balance: Math.round(bal * 100) / 100, title: e.title });
        });
        return { income: inc, expenses: exp, net: inc - exp, chartData: data };
    }, [entries]);

    const recent = [...entries].reverse().slice(0, 8);

    const Stat = ({ icon: Icon, label, value, tone }) => (
        <Glass className="p-4" data-testid={`finance-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center gap-2 mb-1.5">
                <span className="h-7 w-7 rounded-lg flex items-center justify-center" style={{ background: `${tone}1A`, color: tone }}><Icon className="w-3.5 h-3.5" /></span>
                <span className="text-[10px] font-black uppercase tracking-[0.16em] text-white/45">{label}</span>
            </div>
            <p className="font-heading text-2xl font-black" style={{ color: tone }}>{value}</p>
        </Glass>
    );

    return (
        <div className="space-y-5" data-testid="account-finances">
            {/* Header */}
            <div>
                <p className="text-[11px] font-black uppercase tracking-[0.2em] text-[#9D4CDD] mb-1">Your Space</p>
                <h2 className="font-heading text-3xl font-black text-white flex items-center gap-2"><PoundSterling className="w-6 h-6 text-[#9D4CDD]" /> My Finances</h2>
                <p className="text-sm text-white/50 mt-1">A light overview of your Rosterly spend and subscriptions.</p>
            </div>

            {/* Summary stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Stat icon={TrendingUp} label="Total Income" value={gbp(income)} tone="#22C55E" />
                <Stat icon={TrendingDown} label="Total Expenses" value={gbp(expenses)} tone="#EF4444" />
                <Stat icon={Wallet} label="Net Balance" value={gbp(net)} tone={net >= 0 ? "#9D4CDD" : "#F59E0B"} />
            </div>

            {/* Graph */}
            <Glass className="p-5" data-testid="finance-chart-card">
                <h3 className="text-sm font-bold uppercase tracking-wider text-white flex items-center gap-2 mb-4"><TrendingUp className="w-4 h-4 text-[#9D4CDD]" /> Running Balance</h3>
                {chartData.length === 0 ? (
                    <div className="h-[240px] flex items-center justify-center text-sm text-white/40" data-testid="finance-chart-empty">Add a purchase or subscription to build your graph.</div>
                ) : (
                    <ResponsiveContainer width="100%" height={240}>
                        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                            <defs>
                                <linearGradient id="balFill" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#9D4CDD" stopOpacity={0.5} />
                                    <stop offset="100%" stopColor="#9D4CDD" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                            <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} stroke="rgba(255,255,255,0.1)" />
                            <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} stroke="rgba(255,255,255,0.1)" tickFormatter={(v) => `£${v}`} />
                            <Tooltip contentStyle={{ background: "#0E0F16", border: "1px solid #1E2233", borderRadius: 12, color: "#fff" }} formatter={(v) => [gbp(v), "Balance"]} labelStyle={{ color: "rgba(255,255,255,0.5)" }} />
                            <Area type="monotone" dataKey="balance" stroke="#9D4CDD" strokeWidth={2.5} fill="url(#balFill)" />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </Glass>

            {/* Add controls */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Glass className="p-5" data-testid="finance-add-purchase-card">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-white flex items-center gap-2 mb-4"><ShoppingCart className="w-4 h-4 text-[#EF4444]" /> Add Purchase</h3>
                    <div className="space-y-3">
                        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What was it for?" data-testid="finance-purchase-title"
                            className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#9D4CDD]" />
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">£</span>
                            <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" min="0" step="0.01" placeholder="0.00" data-testid="finance-purchase-amount"
                                onKeyDown={(e) => { if (e.key === "Enter") addPurchase(); }}
                                className="w-full bg-black/30 border border-white/10 rounded-lg pl-7 pr-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#9D4CDD]" />
                        </div>
                        <DateInput value={pDate} onChange={setPDate} testid="finance-purchase-date" onEnter={addPurchase} />
                        <button onClick={addPurchase} data-testid="finance-add-purchase-btn"
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#EF4444]/90 hover:bg-[#EF4444] text-white font-bold uppercase tracking-wider text-xs transition-colors">
                            <Plus className="w-4 h-4" /> Add Purchase
                        </button>
                    </div>
                </Glass>

                <Glass className="p-5" data-testid="finance-add-subscription-card">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-white flex items-center gap-2 mb-4"><Sparkles className="w-4 h-4 text-[#22C55E]" /> Subscriptions</h3>
                    <p className="text-xs text-white/45 mb-3">Connect a plan — it adds positive income to your balance.</p>
                    <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-1.5" data-testid="finance-plan-picker">
                            {PLANS.map((p) => (
                                <button key={p.k} onClick={() => setPlan(p.k)} data-testid={`finance-plan-${p.k}`}
                                    className={`px-2 py-2.5 rounded-lg border text-center transition-colors ${plan === p.k ? "border-[#22C55E] bg-[#22C55E]/15 text-white" : "border-white/10 bg-black/25 text-white/60 hover:text-white"}`}>
                                    <span className="block text-[10px] font-black uppercase tracking-wide truncate">{p.label}</span>
                                    <span className="block text-[11px] font-bold text-[#22C55E]">+{gbp(p.price)}</span>
                                </button>
                            ))}
                        </div>
                        <DateInput value={sDate} onChange={setSDate} testid="finance-subscription-date" onEnter={addSubscription} />
                        <button onClick={addSubscription} data-testid="finance-add-subscription-btn"
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#22C55E]/90 hover:bg-[#22C55E] text-black font-bold uppercase tracking-wider text-xs transition-colors">
                            <Plus className="w-4 h-4" /> Add Subscription
                        </button>
                    </div>
                </Glass>
            </div>

            {/* Recent transactions */}
            <Glass className="p-5" data-testid="finance-transactions-card">
                <h3 className="text-sm font-bold uppercase tracking-wider text-white flex items-center gap-2 mb-4"><Wallet className="w-4 h-4 text-[#9D4CDD]" /> Recent Transactions</h3>
                {recent.length === 0 ? (
                    <p className="text-sm text-white/40" data-testid="finance-transactions-empty">No transactions yet.</p>
                ) : (
                    <div className="divide-y divide-white/5" data-testid="finance-transactions-list">
                        {recent.map((e) => {
                            const isInc = e.type === "subscription";
                            return (
                                <div key={e.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`finance-txn-${e.id}`}>
                                    <div className="flex items-center gap-3 min-w-0">
                                        <span className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: isInc ? "#22C55E1A" : "#EF44441A", color: isInc ? "#22C55E" : "#EF4444" }}>
                                            {isInc ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                                        </span>
                                        <div className="min-w-0">
                                            <p className="text-sm text-white truncate">{e.title}</p>
                                            <p className="text-[11px] text-white/40">{isInc ? "Subscription" : "Purchase"} · <EntryDate iso={e.date} onCommit={(ymd) => setDate(e.id, ymd)} testid={`finance-txn-date-${e.id}`} /></p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0">
                                        <span className="font-mono-stat text-sm font-bold" style={{ color: isInc ? "#22C55E" : "#EF4444" }}>{isInc ? "+" : "−"}{gbp(e.amount)}</span>
                                        <button onClick={() => remove(e.id)} data-testid={`finance-txn-delete-${e.id}`} className="text-white/30 hover:text-[#EF4444] transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Glass>
        </div>
    );
}
